import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { Checkbox, ErrorBanner } from '@/components/form';
import { FileDrop, StatusChip } from '@/components/migration/file-drop';
import { GetFromQuickBooks } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { downloadCsv } from '@/lib/imports';
import {
  SIDE,
  fmtAmount,
  isZero,
  readUpload,
  useMigration,
  type MigrationView,
  type OpenItemsResult,
  type Side,
  type TieOut,
  type Upload,
} from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/open-items/$side')({
  beforeLoad: ({ params }) => {
    if (params.side !== 'receivable' && params.side !== 'payable') throw notFound();
  },
  component: OpenItemsPage,
});

/** Screen G9 (document 06): open invoices or unpaid bills at the conversion date. */
function OpenItemsPage() {
  const side = Route.useParams().side as Side;
  const meta = SIDE[side];
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  return (
    <>
      <PageHeader
        title={meta.title}
        description={`Bring over the ${meta.items} still open in QuickBooks the day before the conversion date, so they can be paid and aged here.`}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration">Move from QuickBooks</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState title="Only accounting staff can import open items" />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : m.isError ? (
        <EmptyState title="Could not load the migration" hint={m.error.message} />
      ) : !m.data.conversionDate ? (
        <EmptyState
          title="Set the conversion date first"
          action={
            <Button asChild>
              <Link to="/settings/migration">Move from QuickBooks</Link>
            </Button>
          }
        />
      ) : m.data.status === 'finished' ? (
        <EmptyState
          title="The move from QuickBooks is finished"
          hint="Enter later invoices and bills as new transactions."
        />
      ) : (
        <Wizard key={`${orgId}-${side}`} side={side} view={m.data} />
      )}
    </>
  );
}

function TieStrip({ side, tie }: { side: Side; tie: TieOut }) {
  const off = !isZero(tie.difference);
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border px-4 py-3 text-sm',
        off ? 'border-warning/40 bg-warning/10' : 'border-success/30 bg-success/10',
      )}
      role="status"
      data-testid="tie-out"
    >
      <span>
        {SIDE[side].control} in the trial balance{' '}
        <strong className="tnum">{fmtAmount(tie.expected)}</strong>
      </span>
      <span>
        Open items <strong className="tnum">{fmtAmount(tie.imported)}</strong>
      </span>
      <span>
        Difference <strong className="tnum">{fmtAmount(tie.difference)}</strong>
      </span>
      <StatusChip tone={off ? 'warning' : 'success'}>{off ? 'Does not tie' : 'Ties'}</StatusChip>
    </div>
  );
}

function Wizard({ side, view }: { side: Side; view: MigrationView }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const meta = SIDE[side];
  const [file, setFile] = useState<Upload | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const preview = useMutation({
    mutationFn: async (f: File) => {
      const u = await readUpload(f);
      setFile(u);
      return api<OpenItemsResult>(`/orgs/${orgId}/migration/open-items/${side}/preview`, {
        method: 'POST',
        body: JSON.stringify({ file: u }),
      });
    },
    onMutate: () => setFileError(null),
    onError: (e) => setFileError((e as Error).message),
  });
  const run = useMutation({
    mutationFn: () =>
      api<OpenItemsResult>(`/orgs/${orgId}/migration/open-items/${side}`, {
        method: 'POST',
        body: JSON.stringify({ file }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['migration', orgId] });
      await qc.invalidateQueries({ queryKey: ['sales', orgId] });
      await qc.invalidateQueries({ queryKey: ['purchases', orgId] });
    },
  });
  const result = run.data ?? preview.data;
  const done = Boolean(run.data);
  const rows = result
    ? onlyErrors
      ? result.rows.filter((r) => r.status === 'error')
      : result.rows
    : [];

  const reset = () => {
    setFile(null);
    preview.reset();
    run.reset();
  };

  if (!result)
    return (
      <div className="flex max-w-6xl flex-col gap-4">
        <TieStrip side={side} tie={view.tieOut[side]} />
        <ErrorBanner>{fileError}</ErrorBanner>
        <FileDrop
          title={`Drag and drop the QuickBooks ${meta.report} here, or browse`}
          hint={
            <>
              Reports › {meta.report}, as of <strong>{formatDate(view.openingDate!)}</strong>,
              exported to Excel (.xlsx) or CSV. {meta.who}s are matched by display name, so import
              them first.
            </>
          }
          busy={preview.isPending}
          onFile={(f) => preview.mutate(f)}
        />
        <GetFromQuickBooks
          kind={side === 'receivable' ? 'open-receivables' : 'open-payables'}
          onFile={(f) => preview.mutate(f)}
        />
        {!view.trialBalance ? (
          <p className="text-sm text-muted-foreground">
            The opening trial balance is not imported yet, so there is nothing to tie these items
            to.{' '}
            <Link
              to="/settings/migration/opening-balances"
              className="text-primary hover:underline"
            >
              Import opening balances
            </Link>
          </p>
        ) : null}
      </div>
    );

  return (
    <div className="flex max-w-6xl flex-col gap-4">
      <TieStrip side={side} tie={result.tieOut} />
      <ErrorBanner>{run.error?.message}</ErrorBanner>
      {result.issues.length ? (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          {result.issues.map((i) => (i.row ? `Row ${i.row}: ${i.message}` : i.message)).join(' · ')}
        </div>
      ) : null}
      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          done ? 'border-primary/30 bg-primary/5' : 'bg-card',
        )}
        role="status"
      >
        <span className="font-medium">
          {done
            ? `Imported ${result.ok} of ${result.rows.length} open items (${fmtAmount(result.total)}).`
            : `${result.ok} of ${result.rows.length} open items are ready to import (${fmtAmount(result.total)}).`}
        </span>{' '}
        {result.errors
          ? `${result.errors} with errors ${done ? 'were' : 'will be'} skipped.`
          : 'No errors found.'}{' '}
        {done ? '' : 'The tie-out above shows the result after the import.'}
      </div>
      <div className="flex items-center gap-4">
        <Checkbox
          label="Show only rows with errors"
          checked={onlyErrors}
          onChange={(e) => setOnlyErrors(e.target.checked)}
        />
        {result.errors ? (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() =>
              downloadCsv(`${side}-open-items-errors.csv`, [
                ['Row', 'No.', 'Type', meta.who, 'Date', 'Open balance', 'Errors'],
                ...result.rows
                  .filter((r) => r.status === 'error')
                  .map((r) => [
                    String(r.row),
                    r.number,
                    r.type,
                    r.name,
                    formatDate(r.date),
                    r.open,
                    r.errors.join('; '),
                  ]),
              ])
            }
          >
            <Download className="size-4" /> Download error report
          </Button>
        ) : null}
      </div>
      <div className="max-h-[520px] overflow-y-auto rounded-lg border bg-card">
        <Table>
          <TableHeader className="sticky top-0 bg-card">
            <TableRow>
              <TableHead className="w-[60px] text-right">Row</TableHead>
              <TableHead className="w-[110px]">Date</TableHead>
              <TableHead className="w-[120px]">Type</TableHead>
              <TableHead className="w-[100px]">No.</TableHead>
              <TableHead>{meta.who}</TableHead>
              <TableHead className="w-[110px]">Due date</TableHead>
              <TableHead className="w-[120px] text-right">Amount</TableHead>
              <TableHead className="w-[130px] text-right">Open balance</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.row} className={r.status === 'error' ? 'align-top' : undefined}>
                <TableCell className="tnum text-right text-muted-foreground">{r.row}</TableCell>
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.number}</TableCell>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  {r.errors.map((e) => (
                    <div key={e} className="text-sm text-destructive">
                      {e}
                    </div>
                  ))}
                </TableCell>
                <TableCell>{r.dueDate ? formatDate(r.dueDate) : ''}</TableCell>
                <TableCell className="tnum text-right">{fmtAmount(r.amount)}</TableCell>
                <TableCell className="tnum text-right">
                  {r.kind === 'credit' ? '-' : ''}
                  {fmtAmount(r.open)}
                </TableCell>
                <TableCell>
                  {r.status === 'error' ? (
                    <StatusChip tone="danger">Error</StatusChip>
                  ) : r.status === 'imported' ? (
                    <StatusChip tone="success">Imported</StatusChip>
                  ) : (
                    <StatusChip tone="info">Ready</StatusChip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        {done ? (
          <>
            <Button variant="outline" onClick={reset}>
              Import another file
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={side === 'receivable' ? '/sales/invoices' : '/expenses/bills'}>
                  View {side === 'receivable' ? 'invoices' : 'bills'}
                </Link>
              </Button>
              <Button asChild>
                <Link to="/settings/migration">Back to the checklist</Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={reset}>
              Back
            </Button>
            <Button disabled={run.isPending || result.ok === 0} onClick={() => run.mutate()}>
              {run.isPending
                ? 'Importing…'
                : `Import ${result.ok} ${result.ok === 1 ? 'item' : 'items'}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
